import { Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { ToasterTypes } from 'src/app/core/enum/toaster-type.enum';
import { ReportFilter } from 'src/app/data/model/reportFilter';
import { Tag } from 'src/app/data/model/tag';
import { FilterService } from 'src/app/data/service/filter.service';
import { OrganizationService } from 'src/app/data/service/organization.service';
import { SuppliersService } from 'src/app/data/service/suppliers.service';
import { UserService } from 'src/app/data/service/user.service';
import { ToasterMessageService } from 'src/app/shared/toaster-message/toaster-message.service';
import { PaymentAccount } from '../models/account/payment-account';
import { SupplierShort } from '../models/supplier/supplier-short';
import { TrackingCategory } from '../models/tracking-categories.ts/trackingCategory';
import {
  describeInboxCondition,
  describeInboxConditions,
  inboxConditionsToCriteria,
  inboxCriteriaToConditions,
  inboxDescriptionFromCriteria,
  InboxConditionField,
  InboxConditionOption,
  InboxFieldLabels,
  inboxFieldLabel,
  inboxFieldType,
  InboxFilterCondition,
  inboxOpsFor,
  mergeInboxConditions,
  INBOX_CONDITION_FIELDS,
  INBOX_RANGE_OPS,
  INBOX_STATUS_OPTIONS,
  isInboxConditionComplete,
  UNASSIGNED_TAG,
  UNASSIGNED_TRACKING
} from './inbox-filter-condition';

/**
 * The Inbox filter panel — Bills to Pay's condition builder, pointed at the
 * Inbox's fields. It builds a criteria string and hands it to the page, which
 * runs it through the same server search the Inbox has always used. It can be
 * applied once, or named and saved into the picker on the page title.
 *
 * Status (For Review / Processed) is the first field. The page reads it back out
 * of the criteria and switches the list, the same switch the title picker makes.
 */
@Component({
  selector: 'app-inbox-filter',
  templateUrl: './inbox-filter.component.html',
  styleUrls: ['./inbox-filter.component.scss']
})
export class InboxFilterComponent implements OnInit, OnChanges, OnDestroy {
  @Input() isOpen: boolean = false;
  @Input() accounts: PaymentAccount[] = [];
  @Input() tags: Tag[] = [];
  @Input() selectedTrackingCategories: TrackingCategory[] = [];

  /** Apply the criteria without saving them. */
  @Output() applied = new EventEmitter<string>();
  /** A filter was saved (created or updated); the page applies and lists it. */
  @Output() saved = new EventEmitter<ReportFilter>();
  @Output() deleted = new EventEmitter<string>();
  @Output() closeFilter = new EventEmitter<void>();

  @ViewChild('nameInput') nameInput: ElementRef<HTMLInputElement>;

  draft: InboxFilterCondition[] = [];

  isNaming: boolean = false;
  editingId: string = null;
  draftName: string = '';
  draftDesc: string = '';

  /** Read by the page's table for its column-header filters. */
  suppliers: SupplierShort[] = [];
  users: any[] = [];

  private userDetails: any;
  private filterSeqName: string = '';
  private subscriptions: Subscription[] = [];

  constructor(
    private readonly _suppliersService: SuppliersService,
    private readonly _organizationService: OrganizationService,
    private readonly _toasterMessageService: ToasterMessageService,
    private readonly _filterService: FilterService,
    private readonly _userService: UserService
  ) { }

  ngOnInit(): void {
    this.subscriptions.push(this._userService.loggedUserDetails
      .subscribe(data => this.userDetails = data || []));
    this.loadSuppliers();
    this.loadUsers();
    this.loadNextFilterName();
    this.rebuildOptions();
    this.setDraft([]);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['accounts'] || changes['tags'] || changes['selectedTrackingCategories']) {
      this.rebuildOptions();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  // ── Entry points (called by the page) ─────────────────────────────────────

  openForNew(): void {
    this.resetNaming();
    this.setDraft([]);
  }

  /** The panel opened over an applied, unsaved filter. */
  openForCriteria(criteria: string): void {
    this.resetNaming();
    this.setDraft(inboxCriteriaToConditions(criteria));
  }

  openForEdit(saved: ReportFilter): void {
    this.editingId = saved.id;
    this.isNaming = true;
    this.draftName = saved.name || '';
    this.setDraft(inboxCriteriaToConditions(saved.searchCriteria));
    this.draftDesc = inboxDescriptionFromCriteria(saved.searchCriteria) || this.conditionsSentence();
  }

  /** A column header's filter icon: start a condition on that field. */
  openForField(field: InboxConditionField, criteria: string = ''): void {
    this.resetNaming();
    const existing = mergeInboxConditions(inboxCriteriaToConditions(criteria));
    // A field already filtered on reopens for more values rather than as a second row.
    const index = existing.findIndex(c => c.field === field);
    if (index >= 0) {
      existing[index] = { ...existing[index], confirmed: false };
      this.draft = existing;
      return;
    }
    this.draft = existing.concat([this.newCondition(field)]);
  }

  clear(): void {
    this.resetNaming();
    this.setDraft([]);
  }

  // ── Condition editing ─────────────────────────────────────────────────────

  private setDraft(conditions: InboxFilterCondition[]): void {
    const usable = mergeInboxConditions((conditions || []).filter(c => c && this.isFieldAvailable(c.field)));
    this.draft = usable.length ? usable : [this.newCondition()];
  }

  private newCondition(field: InboxConditionField = 'state'): InboxFilterCondition {
    const safeField = this.isFieldAvailable(field) ? field : this.availableFields[0]?.value || 'state';
    return {
      field: safeField,
      op: inboxOpsFor(safeField)[0]?.value,
      value: '',
      values: [],
      valueTo: '',
      confirmed: false
    };
  }

  get hasOpenCondition(): boolean {
    return this.draft.some(c => !c?.confirmed);
  }

  get addConditionTitle(): string {
    return this.hasOpenCondition ? 'Finish the open condition first' : 'Add another condition';
  }

  addCondition(): void {
    if (this.hasOpenCondition) return;
    this.draft = this.draft.concat([this.newCondition('date')]);
  }

  removeCondition(index: number): void {
    this.setDraft(this.draft.filter((_c, i) => i !== index));
  }

  confirmCondition(index: number): void {
    if (!isInboxConditionComplete(this.draft[index])) return;
    this.draft[index] = { ...this.draft[index], confirmed: true };
  }

  editCondition(index: number): void {
    this.draft[index] = { ...this.draft[index], confirmed: false };
  }

  onFieldChange(index: number, field: InboxConditionField): void {
    this.draft[index] = this.newCondition(field);
  }

  onOpChange(index: number, op: string): void {
    this.draft[index] = { ...this.draft[index], op, valueTo: '' };
  }

  onValueChange(index: number, value: string): void {
    this.draft[index] = { ...this.draft[index], value };
  }

  onValueToChange(index: number, valueTo: string): void {
    this.draft[index] = { ...this.draft[index], valueTo };
  }

  /** A list field's choices. `value` keeps the first for anything reading one value. */
  onValuesChange(index: number, values: string[]): void {
    this.draft[index] = { ...this.draft[index], values, value: values[0] || '' };
  }

  private static readonly NO_VALUES: string[] = [];

  valuesOf(condition: InboxFilterCondition): string[] {
    return condition?.values || InboxFilterComponent.NO_VALUES;
  }

  /** "Or" when an earlier row is on the same field — that is how the search
   *  combines them, so the panel should read that way too. */
  stepLabel(index: number): string {
    if (index === 0) return 'Condition';
    const field = this.draft[index]?.field;
    return this.draft.slice(0, index).some(c => c?.field === field) ? 'Or' : 'And';
  }

  summaryOf(condition: InboxFilterCondition): string {
    return describeInboxCondition(condition, this.fieldLabels);
  }

  opsFor(condition: InboxFilterCondition): InboxConditionOption[] {
    return inboxOpsFor(condition?.field);
  }

  isRangeOp(condition: InboxFilterCondition): boolean {
    return INBOX_RANGE_OPS.includes(condition?.op);
  }

  showSelectValue(condition: InboxFilterCondition): boolean {
    return inboxFieldType(condition?.field) === 'enum';
  }

  showDateValue(condition: InboxFilterCondition): boolean {
    return inboxFieldType(condition?.field) === 'date';
  }

  showNumberValue(condition: InboxFilterCondition): boolean {
    return inboxFieldType(condition?.field) === 'number';
  }

  isComplete(condition: InboxFilterCondition): boolean {
    return isInboxConditionComplete(condition);
  }

  trackByIndex(index: number): number {
    return index;
  }

  // ── Option sources ────────────────────────────────────────────────────────
  // Same arrays between change-detection runs: the value selects are bound to
  // ngModel, and a fresh options array each tick tears them down under it.

  private static readonly NO_OPTIONS: InboxConditionOption[] = [];
  private options: { [field: string]: InboxConditionOption[] } = {};
  private fieldsCache: { value: InboxConditionField; label: string }[] = [];

  valueOptionsFor(condition: InboxFilterCondition): InboxConditionOption[] {
    return this.options[condition?.field] || InboxFilterComponent.NO_OPTIONS;
  }

  get availableFields(): { value: InboxConditionField; label: string }[] {
    return this.fieldsCache;
  }

  private isFieldAvailable(field: InboxConditionField): boolean {
    return this.fieldsCache.some(f => f.value === field);
  }

  private get fieldLabels(): InboxFieldLabels {
    return {
      job: this.selectedTrackingCategories?.[0]?.name,
      job2: this.selectedTrackingCategories?.[1]?.name
    };
  }

  private trackingOptions(category: TrackingCategory): InboxConditionOption[] {
    if (!category) return [];
    return [{ value: UNASSIGNED_TRACKING, label: 'Unassigned' }]
      .concat((category.options || []).filter(o => o?.name).map(o => ({ value: o.name, label: o.name })));
  }

  private rebuildOptions(): void {
    const byName = (list: { name?: string }[]) => (list || [])
      .filter(item => item?.name)
      .map(item => ({ value: item.name, label: item.name }))
      .sort((a, b) => a.label.localeCompare(b.label));

    this.options = {
      state: INBOX_STATUS_OPTIONS,
      supplier: byName(this.suppliers),
      category: byName(this.accounts),
      tag: (this.tags || []).filter(t => t?.tagName).map(t => t.tagName === 'Unassigned'
        ? { value: UNASSIGNED_TAG, label: 'Unassigned' }
        : { value: t.tagName, label: t.tagName }),
      job: this.trackingOptions(this.selectedTrackingCategories?.[0]),
      job2: this.trackingOptions(this.selectedTrackingCategories?.[1]),
      submitter: byName(this.users),
      assignee: byName(this.users)
    };

    // A field with nothing to choose from is left out, and so is the second
    // job field when the organisation tracks only one category.
    const labels = this.fieldLabels;
    this.fieldsCache = INBOX_CONDITION_FIELDS
      .filter(f => f.type !== 'enum' || (this.options[f.value] || []).length > 0)
      .map(f => ({ value: f.value, label: inboxFieldLabel(f.value, labels) }));

    // Conditions opened before their options arrived pick them up now.
    if (this.draft.length && this.draft.every(c => !c.confirmed && !c.value)) {
      this.draft = this.draft.map(c => this.isFieldAvailable(c.field) ? c : this.newCondition());
    }
  }

  // ── Panel state ───────────────────────────────────────────────────────────

  private get confirmedConditions(): InboxFilterCondition[] {
    return this.draft.filter(c => c.confirmed && isInboxConditionComplete(c));
  }

  get isEditingSaved(): boolean {
    return !!this.editingId;
  }

  get panelTitle(): string {
    return this.editingId ? 'Edit filter' : 'Filters';
  }

  get saveLabel(): string {
    return this.editingId ? 'Save changes' : 'Save & apply';
  }

  get canApply(): boolean {
    return this.confirmedConditions.length > 0;
  }

  get ruleText(): string {
    return this.conditionsSentence();
  }

  private conditionsSentence(): string {
    return describeInboxConditions(this.confirmedConditions, this.fieldLabels);
  }

  startNaming(): void {
    if (!this.canApply) return;
    this.isNaming = true;
    if (!this.draftDesc?.trim()) {
      this.draftDesc = this.conditionsSentence();
    }
    setTimeout(() => this.nameInput?.nativeElement.focus());
  }

  cancelNaming(): void {
    this.isNaming = false;
    this.draftName = '';
    this.draftDesc = '';
  }

  private resetNaming(): void {
    this.editingId = null;
    this.isNaming = false;
    this.draftName = '';
    this.draftDesc = '';
  }

  // ── Commit ────────────────────────────────────────────────────────────────

  applyFilter(): void {
    if (!this.canApply) return;
    this.applied.emit(inboxConditionsToCriteria(this.confirmedConditions));
  }

  saveFilter(): void {
    if (!this.canApply) return;

    const filter = {
      id: this.editingId || null,
      name: this.draftName?.trim() || this.filterSeqName || 'Untitled filter',
      type: 0,
      isPinned: true,
      searchCriteria: inboxConditionsToCriteria(this.confirmedConditions, this.draftDesc),
      createdBy: this.userDetails?.id,
      createdDate: new Date(),
      filterPageType: 1,
      organizationId: this._organizationService.selectedOrganization
    } as ReportFilter;

    this._filterService.SaveFilter('', filter)
      .pipe(take(1))
      .subscribe(
        (createdId: string) => {
          filter.id = this.editingId || createdId;
          this._toasterMessageService.displayToasterState(ToasterTypes.Success, 'Success', 'Filter saved.');
          this.saved.emit(filter);
          this.resetNaming();
          this.loadNextFilterName();
        },
        (e: Error) => this._toasterMessageService.displayToasterState(ToasterTypes.Error, 'Error', e.message));
  }

  deleteFilter(): void {
    if (!this.editingId) return;
    const id = this.editingId;

    this._filterService.DeleteFilter('Filter/DeleteFilter', id)
      .pipe(take(1))
      .subscribe(
        () => {
          this._toasterMessageService.displayToasterState(ToasterTypes.Success, 'Success', 'Filter deleted.');
          this.clear();
          this.deleted.emit(id);
        },
        (e: Error) => this._toasterMessageService.displayToasterState(ToasterTypes.Error, 'Error', e.message));
  }

  close(): void {
    this.closeFilter.emit();
  }

  // ── Lookups ───────────────────────────────────────────────────────────────

  private loadSuppliers(): void {
    this._suppliersService.getSuppliers(this._organizationService.selectedOrganization)
      .pipe(take(1))
      .subscribe((suppliers: SupplierShort[]) => {
        this.suppliers = (suppliers || []).filter(s => s?.name).sort((a, b) => a.name.localeCompare(b.name));
        this.rebuildOptions();
      }, () => { });
  }

  private loadUsers(): void {
    this._userService.getUsersByOrganizationId(this._organizationService.selectedOrganization)
      .pipe(take(1))
      .subscribe(response => {
        this.users = response?.data || [];
        this.rebuildOptions();
      }, () => { });
  }

  private loadNextFilterName(): void {
    this._filterService.getLatestFilterName('Filter/GetLatestFilterNumber', 1, this._organizationService.selectedOrganization)
      .pipe(take(1))
      .subscribe(name => this.filterSeqName = name?.data || '', () => { });
  }
}
